import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from "react";

/** Pause autoplay when less than this fraction of the preview is visible. */
const MIN_VISIBLE_INTERSECTION_RATIO = 0.4;

/** Resume autoplay after page scroll is idle for this long. */
const PAGE_SCROLL_IDLE_MS = 200;
export type UseLandingHeroPreviewPlaybackGateParams = {
  previewRootRef: RefObject<HTMLElement | null>;
  /** OverlayScrollbars viewport for the page (not nested preview scrollers). */
  pageScrollViewport: HTMLElement | null;
};
export type UseLandingHeroPreviewPlaybackGateResult = {
  /** When true, hero preview autoplay and auto-replay should not run. */
  isPlaybackSuppressed: boolean;
};

/**
 * Suppresses landing hero preview playback while the user scrolls the page or
 * when the preview is mostly off-screen — reduces jank from Redux + layout
 * updates fighting scroll compositing.
 */
export const useLandingHeroPreviewPlaybackGate = t0 => {
  const $ = _c(8);
  const {
    previewRootRef,
    pageScrollViewport
  } = t0;
  const [isSufficientlyVisible, setIsSufficientlyVisible] = useState(true);
  const [isPageScrolling, setIsPageScrolling] = useState(false);
  let t1;
  let t2;
  if ($[0] !== previewRootRef) {
    t1 = () => {
      const element = previewRootRef.current;
      if (!element) {
        return;
      }
      if (typeof IntersectionObserver !== "function") {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setIsSufficientlyVisible(entry.isIntersecting && entry.intersectionRatio >= MIN_VISIBLE_INTERSECTION_RATIO);
      }, {
        threshold: [0, 0.25, 0.4, 0.5, 0.75, 1]
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t2 = [previewRootRef];
    $[0] = previewRootRef;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== pageScrollViewport) {
    t3 = () => {
      if (!pageScrollViewport) {
        return;
      }
      let idleTimeoutId = null;
      const markPageScrolling = () => {
        setIsPageScrolling(true);
        if (idleTimeoutId !== null) {
          clearTimeout(idleTimeoutId);
        }
        idleTimeoutId = setTimeout(() => {
          idleTimeoutId = null;
          setIsPageScrolling(false);
        }, PAGE_SCROLL_IDLE_MS);
      };
      pageScrollViewport.addEventListener("scroll", markPageScrolling, {
        passive: true
      });
      return () => {
        pageScrollViewport.removeEventListener("scroll", markPageScrolling);
        if (idleTimeoutId !== null) {
          clearTimeout(idleTimeoutId);
        }
      };
    };
    t4 = [pageScrollViewport];
    $[3] = pageScrollViewport;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const t5 = !isSufficientlyVisible || isPageScrolling;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      isPlaybackSuppressed: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
};